import React from 'react';
import { defineMessages } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import WarningOutlineIcon from '@atlaskit/icon-lab/core/warning-outline';
import { token } from '@atlaskit/tokens';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

const i18n = defineMessages({
	errorTitle: {
		id: 'editor-offline-storage.version-insertion-error-flag.title',
		defaultMessage: 'Error saving version history',
		description: 'Title for the error flag when version insertion fails',
	},
	errorTitleRetry: {
		id: 'editor-offline-storage.version-insertion-error-flag.title-retry',
		defaultMessage: 'Failed to save',
		description:
			'Title for the error flag when version insertion fails after the user manually retries',
	},
	errorDescription: {
		id: 'editor-offline-storage.version-insertion-error-flag.description',
		defaultMessage:
			"We couldn't save a version history of your offline edits. Your changes are safe, but if you want a backup please try again.",
		description:
			'Description for the error flag when version insertion fails initially and offline edits are not backed up',
	},
	errorDescriptionRetry: {
		id: 'editor-offline-storage.version-insertion-error-flag.description-retry',
		defaultMessage:
			"We're still having trouble saving a version history of your offline edits. Please try again.",
		description:
			'Description for the error flag when version insertion fails after the user manually retries',
	},
	retryButton: {
		id: 'editor-offline-storage.version-insertion-error-flag.retry-button',
		defaultMessage: 'Try again',
		description: 'Button to retry backing up the offline edits',
	},
	successTitle: {
		id: 'editor-offline-storage.version-insertion-success-flag.title',
		defaultMessage: 'Version history saved',
		description:
			'Title for the success flag when version insertion succeeds after the user manually retries',
	},
	successDescription: {
		id: 'editor-offline-storage.version-insertion-success-flag.description',
		defaultMessage: 'Your offline edits have been saved to version history.',
		description:
			'Description for the success flag when version insertion succeeds after the user manually retries',
	},
});

const ERROR_FLAG_ID = 'version-insertion-error-flag';
const SUCCESS_FLAG_ID = 'version-insertion-success-flag';

export const showVersionInsertionErrorFlag = (
	flags: FlagsStateContainer | undefined,
	intl: IntlShape,
	onRetry: () => void,
	onClose: () => void,
	manualRetryCount: number,
): void => {
	if (!flags) {
		return;
	}
	void flags.showCustomFlag({
		id: ERROR_FLAG_ID,
		title: intl.formatMessage(manualRetryCount > 0 ? i18n.errorTitleRetry : i18n.errorTitle),
		description: intl.formatMessage(
			manualRetryCount > 0 ? i18n.errorDescriptionRetry : i18n.errorDescription,
		),
		customIcon: <WarningOutlineIcon color={token('color.icon')} label="" />,
		actions: [
			{
				content: intl.formatMessage(i18n.retryButton),
				onClick: onRetry,
			},
		],
		onClose,
	});
};

export const showVersionInsertionSuccessFlag = (
	flags: FlagsStateContainer | undefined,
	intl: IntlShape,
): void => {
	if (!flags) {
		return;
	}
	void flags.showCustomFlag({
		id: SUCCESS_FLAG_ID,
		title: intl.formatMessage(i18n.successTitle),
		description: intl.formatMessage(i18n.successDescription),
		customIcon: <StatusSuccessIcon color={token('color.icon.success')} label="" />,
	});
};

export const hideVersionInsertionErrorFlag = (flags: FlagsStateContainer | undefined): void => {
	if (!flags) {
		return;
	}
	void flags.hideFlag(ERROR_FLAG_ID);
};
